/**
 * Base class for transform handles.
 *
 * Extends AbstractHandle with transform-specific functionality:
 * - Entity transforms (translate, rotate, scale)
 * - Ghost entity management using ODA view.add() pattern
 * - Transform command execution for undo/redo
 *
 * Performance optimization:
 * Uses the ODA pattern of adding ghosts directly to the view (not database)
 * during drag operations. This avoids recordGraphicsModified() overhead
 * and provides smooth interactive feedback.
 *
 * Pattern:
 * 1. start(): Create ghost, add to view with view.add(), erase original from view
 * 2. ongoing(): Transform ghost directly (no recordGraphicsModified)
 * 3. end(): Erase ghost from view, apply transform to original in database
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import { trackShapeChange } from "@/analytics/drawing-analytics";
import { isPhotoMarkerEntity } from "../../photomarker/detection";
import { readEntityMeta } from "../../meta/xdata";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type {
  TransformContext,
  TransformData,
  Point3D,
  KeyPoints,
} from "../types";
import { HandlePosition } from "../types";
import { AbstractHandle } from "./AbstractHandle";
import { deleteObj } from "../../core/delete";
import { openAs } from "../../core/oda";
import { createViewGhost } from "../../entities/ghost";
import { matrixToArray } from "../matrix";
import {
  applyTransformToEntity,
  applyTransformToEntityIds,
  createTransform,
  isIdentityTransform,
  snapshotTransform,
  transformDelta,
  type EntityTransform,
  type EntityTransformParams,
} from "../entityTransform";
import { TransformEntityCommand } from "../../commands/TransformEntityCommand";
import { Logger } from "@/logger";

/**
 * Base implementation for transform handles.
 */
export abstract class BaseHandle extends AbstractHandle {
  protected entityIds: DrawingModule.OdDbObjectId[];
  protected keyPoints: KeyPoints;

  /** Transform data for each entity */
  protected transformData: TransformData[] = [];

  /**
   * The transform accumulated since start(). Subclasses write to it through
   * setAccumulated() and never mutate its points in place — the same object
   * is snapshotted into the undo command at end().
   */
  protected accumulated: EntityTransform = createTransform();

  /**
   * The transform the entities currently reflect, i.e. as of the previous
   * frame. setGhostTransforms() advances it once the delta has been applied.
   */
  protected previous: EntityTransform = createTransform();

  /** Throttle state for updates during drag */
  private lastUpdateTime = 0;
  private readonly UPDATE_INTERVAL_MS = 16; // ~60fps max

  /** Whether to use the view-based ghost system for performance */
  protected useViewGhosts = true;

  /**
   * When true, a committed transform reports moved photo markers and disorders
   * to analytics. Only TranslationHandle opts in: a drag of one of those is
   * the "drag & drop" the usage dashboard cares about, whereas resize/rotate
   * handles answer a different question.
   */
  protected tracksMoveAnalytics = false;

  /**
   * Update the accumulated transform. The single write path for subclasses,
   * which each drive one channel: ongoing() recomputes that channel from the
   * pointer position and leaves the others at their neutral value.
   */
  protected setAccumulated(partial: Partial<EntityTransform>): void {
    this.accumulated = { ...this.accumulated, ...partial };
  }

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityIds: DrawingModule.OdDbObjectId[],
    keyPoints: KeyPoints,
    position: HandlePosition,
  ) {
    super(viewer, history);
    this.entityIds = entityIds;
    this.keyPoints = keyPoints;
    this.worldPosition = this.getPositionFromKeyPoints(position);
  }

  /**
   * Get world position from key points based on position identifier.
   */
  protected getPositionFromKeyPoints(position: HandlePosition): Point3D {
    switch (position) {
      case HandlePosition.Center:
        return this.keyPoints.center;
      case HandlePosition.TopLeft:
        return this.keyPoints.topLeft;
      case HandlePosition.TopRight:
        return this.keyPoints.topRight;
      case HandlePosition.BottomLeft:
        return this.keyPoints.bottomLeft;
      case HandlePosition.BottomRight:
        return this.keyPoints.bottomRight;
      case HandlePosition.TopMiddle:
        return this.keyPoints.topMiddle;
      case HandlePosition.BottomMiddle:
        return this.keyPoints.bottomMiddle;
      case HandlePosition.LeftMiddle:
        return this.keyPoints.leftMiddle;
      case HandlePosition.RightMiddle:
        return this.keyPoints.rightMiddle;
      default:
        return this.keyPoints.center;
    }
  }

  /** Original anti-aliasing mode to restore after drag */
  private originalAAMode: number = 0;

  /**
   * Start transform - create ghosts and add to view.
   *
   * Uses ODA pattern: ghost entities are added directly to the view (not database)
   * for fast interactive preview. Originals are erased from view during drag.
   */
  start(): void {
    if (this.isTransforming) return;
    this.isTransforming = true;

    // Reset throttle timer
    this.lastUpdateTime = 0;

    // Reset accumulated and previous transforms
    this.accumulated = createTransform();
    this.previous = createTransform();

    const db = this.viewer.database;
    if (!db) return;

    // Get the active view for adding/erasing drawables
    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // PERFORMANCE: Enable interactive mode and disable AA during drag
    if (view) {
      // Store original AA mode and disable it for faster rendering
      this.originalAAMode = view.antiAliasingMode();
      view.enableAntiAliasing(0);

      // Try interactive mode (may not work in all WebAssembly builds)
      try {
        if (!view.isInInteractivity()) {
          view.beginInteractivity(60); // Target 60fps
        }
      } catch {
        // beginInteractivity not supported in this build
      }
    }

    // Create transform data for each entity
    this.transformData = this.entityIds.map((entityId) => {
      const handle = this.getEntityHandle(entityId);
      const originalMatrix = this.getEntityMatrix(entityId);

      const data: TransformData = {
        entityId,
        handle,
        originalMatrix,
      };

      // If using view ghosts, create ghost and swap visibility
      if (this.useViewGhosts && view) {
        const ghost = this.createGhostForView(entityId);
        if (ghost) {
          data.ghostEntity = ghost;

          // Add ghost to view (not database) - this is the key ODA pattern
          // Pass null for model as per ODA examples (cast needed for TypeScript)
          view.add(ghost, null as unknown as DrawingModule.OdGsModel);
        }
      }

      return data;
    });

    // Force initial update to show ghosts
    if (this.useViewGhosts) {
      const device = this.viewer.app?.getDevice();
      device?.invalidate();
    }
  }

  /**
   * Ongoing transform - update ghost entities.
   * Subclasses must implement to apply specific transform.
   */
  abstract ongoing(context: TransformContext): void;

  /**
   * End transform - finalize and execute command for undo/redo.
   *
   * When using view ghosts:
   * 1. Remove ghosts from view
   * 2. Re-add originals to view
   * 3. Apply accumulated transform to originals in database
   * 4. Execute command for undo/redo
   */
  end(): void {
    if (!this.isTransforming) return;

    this.isTransforming = false;

    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // PERFORMANCE: Restore AA mode and end interactive mode
    if (view) {
      try {
        if (view.isInInteractivity()) {
          view.endInteractivity();
        }
      } catch {
        Logger.captureException(
          new Error(`BaseHandle.ts : endInteractivity is not supported`),
        );
      }
      view.enableAntiAliasing(this.originalAAMode);
    }

    // Collect ghosts to delete AFTER the render (see below).
    // deleteObj() must not be called before App.Update() because view.erase()
    // only queues the removal — ODA's render pipeline still holds the pointer
    // until the next frame. Freeing early → null vtable → "null function" crash.
    const entityHandlesForLog = this.transformData
      .map((d) => d.handle)
      .join(",");
    const ghostsToDelete: DrawingModule.OdDbEntity[] = [];

    // Clean up ghosts and restore originals
    for (const data of this.transformData) {
      if (this.useViewGhosts && data.ghostEntity && view) {
        // Remove ghost from view (deferred memory release — see below)
        view.erase(data.ghostEntity);
        ghostsToDelete.push(data.ghostEntity);
        data.ghostEntity = undefined;
      }

      // Apply accumulated transform to original entity in database
      this.applyAccumulatedTransformToEntity(data.entityId);

      // Mark original for final render
      const entity = openAs(data.entityId, this.module.OdDbEntity, 1);
      entity.recordGraphicsModified(true);
      deleteObj(entity);
    }

    // Classified before transformData is cleared below — afterwards the
    // entity ids are gone. Never lets analytics break a transform commit.
    if (this.tracksMoveAnalytics && !isIdentityTransform(this.accumulated)) {
      this.reportMovedEntities();
    }

    // Undo/redo replays the very transform the forward apply above used: the
    // params carry it as-is, so there is no encoding for the two to disagree
    // on. Snapshotted because start()/cancel() reset this.accumulated.
    const transform = snapshotTransform(this.accumulated);
    const params: EntityTransformParams[] = this.transformData.map((data) => ({
      handle: data.handle,
      transform,
    }));

    // Clear transform data
    this.transformData = [];

    // Execute command if there was actual transform
    const commandExecuted =
      params.length > 0 && !isIdentityTransform(transform);
    if (commandExecuted) {
      const command = new TransformEntityCommand(this.viewer, params);
      this.history.execute(command);
    }

    device?.invalidate();
    try {
      this.safeViewerUpdate(`${this.constructor.name}.end`, {
        entityHandles: entityHandlesForLog,
        entityCount: params.length,
        ghostCount: ghostsToDelete.length,
        commandExecuted,
        useViewGhosts: this.useViewGhosts,
        transform,
        deviceNull: !device,
        viewNull: !view,
      });
    } finally {
      // Safe to free ghost memory now: App.Update() has completed so ODA has
      // fully processed view.erase() and no longer holds any pointer to the ghosts.
      // Must run even if safeViewerUpdate threw, otherwise these wrappers leak
      // and leave the WASM module in a corrupted state for subsequent operations.
      for (const ghost of ghostsToDelete) {
        deleteObj(ghost);
      }
    }
  }

  /**
   * Cancel transform - restore original state.
   *
   * When using view ghosts: Simply remove ghosts and re-add originals.
   * The originals were never transformed, so no reversal needed.
   *
   * When not using view ghosts: Reverse the accumulated transform.
   */
  cancel(): void {
    if (!this.isTransforming) return;

    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // PERFORMANCE: Restore AA mode and end interactive mode
    if (view) {
      try {
        if (view.isInInteractivity()) {
          view.endInteractivity();
        }
      } catch {
        // endInteractivity not supported
      }
      view.enableAntiAliasing(this.originalAAMode);
    }

    const entityHandlesForLog = this.transformData
      .map((d) => d.handle)
      .join(",");
    const ghostsToDelete: DrawingModule.OdDbEntity[] = [];
    for (const data of this.transformData) {
      if (this.useViewGhosts && data.ghostEntity && view) {
        // Simply remove ghost from view - original was never modified
        view.erase(data.ghostEntity);
        ghostsToDelete.push(data.ghostEntity);
        data.ghostEntity = undefined;
      } else {
        // Fallback: no ghost was created, so the original itself was modified
        // in place during the drag — reverse the accumulated transform on it.
        // Reverse channel order (scale, rotation, translation) is what
        // correctly undoes the forward composition.
        applyTransformToEntityIds(
          this.module,
          [data.entityId],
          this.accumulated,
          { invert: true, order: "reverse", recordGraphics: false },
        );
      }
    }

    // Reset accumulated and previous transforms
    this.accumulated = createTransform();
    this.previous = createTransform();

    this.isTransforming = false;
    this.transformData = [];

    device?.invalidate();
    try {
      this.safeViewerUpdate(`${this.constructor.name}.cancel`, {
        entityHandles: entityHandlesForLog,
        ghostCount: ghostsToDelete.length,
        useViewGhosts: this.useViewGhosts,
        deviceNull: !device,
        viewNull: !view,
      });
    } finally {
      for (const ghost of ghostsToDelete) deleteObj(ghost);
    }
  }

  // ============== Protected Helper Methods ==============

  /**
   * Apply transform to ghost entities using delta from previous frame.
   * This avoids drift by computing incremental transforms.
   *
   * Key performance optimization: When using view ghosts, we transform
   * the ghost entity directly without recordGraphicsModified(), which
   * is the main cause of slow updates on large drawings.
   */
  protected setGhostTransforms(): void {
    const delta = transformDelta(this.previous, this.accumulated);

    for (const data of this.transformData) {
      if (data.ghostEntity) {
        // Ghosts live in the view, not the database: no recordGraphicsModified()
        // (expensive, and unnecessary here) and no photo marker counter-rotation.
        applyTransformToEntity(this.module, data.ghostEntity, delta);
      } else {
        // No ghost could be created — transform the original in place instead,
        // which does need both of the above.
        applyTransformToEntityIds(this.module, [data.entityId], delta);
      }
    }

    // Update previous values for next frame
    this.previous = snapshotTransform(this.accumulated);
  }

  /**
   * Throttled update - limits viewer updates to ~60fps during drag.
   * Returns true if update was performed, false if throttled.
   */
  protected throttledUpdate(): boolean {
    const now = performance.now();
    if (now - this.lastUpdateTime >= this.UPDATE_INTERVAL_MS) {
      this.viewer.update();
      this.lastUpdateTime = now;
      return true;
    }
    return false;
  }

  /**
   * Create a ghost entity for view-based rendering.
   *
   * Unlike the database-based approach, this creates a clone that is NOT
   * added to the database. It will be added directly to the view for
   * fast interactive rendering.
   *
   * @returns The cloned entity (not an ID since it's not in database)
   */
  protected createGhostForView(
    entityId: DrawingModule.OdDbObjectId,
  ): DrawingModule.OdDbEntity | undefined {
    return createViewGhost(this.module, this.viewer, entityId);
  }

  /**
   * Apply accumulated transform to an entity in the database.
   * Called at end() to persist the transform.
   */
  /**
   * Report photo markers and disorders among the entities just transformed
   * (cf. tracksMoveAnalytics). A disorder is any entity whose DR_META xData
   * carries a defectName — the identity written at draw time.
   */
  private reportMovedEntities(): void {
    try {
      // One event per gesture, not per entity: moving a selection of twenty
      // markers is one action. The entity count rides along in the context.
      let photoMarkers = 0;
      let defects = 0;
      for (const data of this.transformData) {
        if (isPhotoMarkerEntity(this.module, data.entityId)) {
          photoMarkers++;
          continue;
        }
        const meta = readEntityMeta(this.module, data.entityId);
        if (meta?.defectName) defects++;
      }
      if (photoMarkers > 0) {
        trackShapeChange("photo_marker_moved", { count: photoMarkers });
      }
      if (defects > 0) trackShapeChange("defect_moved", { count: defects });
    } catch {
      // Analytics must never break a transform commit.
    }
  }

  protected applyAccumulatedTransformToEntity(
    entityId: DrawingModule.OdDbObjectId,
  ): void {
    // recordGraphics: false — end() marks the entity itself right afterwards,
    // in its own loop over transformData.
    applyTransformToEntityIds(this.module, [entityId], this.accumulated, {
      recordGraphics: false,
    });
  }

  /**
   * Get entity transform matrix as array.
   */
  protected getEntityMatrix(entityId: DrawingModule.OdDbObjectId): number[] {
    const entity = openAs(entityId, this.module.OdDbEntity, 0);
    // For block references, get block transform
    const blockRef = this.module.OdDbBlockReference.cast(entity);
    if (blockRef) {
      const matrix = blockRef.blockTransform();
      const result = matrixToArray(matrix);
      deleteObj(matrix);
      deleteObj(entity);
      return result;
    }
    // For other entities, return identity (they don't have a transform matrix)
    const identity = new this.module.OdGeMatrix3d();
    identity.setToIdentity();
    const result = matrixToArray(identity);
    deleteObj(identity);
    deleteObj(entity);
    return result;
  }

  /**
   * Set entity transparency.
   * if visible, set to 1, if false 0.5
   */
  protected setEntityTransparency(
    entityId: DrawingModule.OdDbObjectId,
    visible: boolean,
  ): void {
    const entity = openAs(entityId, this.module.OdDbEntity, 1);
    const transparency = entity.transparency();
    transparency.setAlpha(visible ? 1 : 0.5);
    entity.setTransparency(transparency, true);
    deleteObj(transparency);
    deleteObj(entity);
  }

  /**
   * Set entity visibility (fully show/hide).
   */
  protected setEntityVisibility(
    entityId: DrawingModule.OdDbObjectId,
    visible: boolean,
  ): void {
    const entity = openAs(entityId, this.module.OdDbEntity, 1);
    // Visibility enum: kVisible = 0, kInvisible = 1
    // Second param: doSubents (apply to sub-entities)
    entity.setVisibility(visible ? 0 : 1, true);
    deleteObj(entity);
  }
}
